import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  LayoutDashboard,
  Users,
  Calendar,
  AlertTriangle,
  UserCircle,
  GraduationCap,
  FileText,
  ClipboardCheck,
  FileSpreadsheet,
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  X,
  SlidersHorizontal,
  Book,
  ClipboardList,
} from "lucide-react";

// Navigation items with RBAC
const NAV_ITEMS = [
  {
    title: "Dashboard",
    href: "/",
    icon: LayoutDashboard,
    roles: ["Admin", "ComplianceManager", "Scheduler", "Caregiver"],
  },
  {
    title: "Survey Readiness",
    href: "/survey-dashboard",
    icon: ClipboardCheck,
    roles: ["Admin", "ComplianceManager", "Scheduler"],
  },
  {
    title: "Audit Module",
    href: "/audits",
    icon: ClipboardList,
    roles: ["Admin", "ComplianceManager", "Scheduler", "RNSupervisor"],
  },
  {
    title: "Clients",
    href: "/clients",
    icon: Users,
    roles: ["Admin", "ComplianceManager", "Scheduler"],
  },
  {
    title: "Schedule",
    href: "/schedule",
    icon: Calendar,
    roles: ["Admin", "ComplianceManager", "Scheduler", "Caregiver"],
  },
  {
    title: "EVV Inbox",
    href: "/evv-exceptions",
    icon: AlertTriangle,
    roles: ["Admin", "ComplianceManager", "Scheduler", "Caregiver"],
  },
  {
    title: "Sandata Import",
    href: "/sandata-import",
    icon: FileSpreadsheet,
    roles: ["Admin", "ComplianceManager"],
  },
  {
    title: "Personnel",
    href: "/personnel",
    icon: UserCircle,
    roles: ["Admin", "ComplianceManager"],
  },
  {
    title: "Training Matrix",
    href: "/training-matrix",
    icon: GraduationCap,
    roles: ["Admin", "ComplianceManager"],
  },
  {
    title: "Documents",
    href: "/documents",
    icon: FileText,
    roles: ["Admin", "ComplianceManager", "Scheduler"],
  },
  {
    title: "Users",
    href: "/users",
    icon: Settings,
    roles: ["Admin"],
  },
  {
    title: "Audit Logs",
    href: "/audit-logs",
    icon: Shield,
    roles: ["Admin", "ComplianceManager"],
  },
  {
    title: "System Settings",
    href: "/system-settings",
    icon: SlidersHorizontal,
    roles: ["Admin", "ComplianceManager"],
  },
  {
    title: "Knowledge Base",
    href: "/knowledge-base",
    icon: Book,
    roles: ["Admin", "ComplianceManager", "Scheduler", "Caregiver"],
  },
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_avida-compliance-2/artifacts/bdnybt8a_Avida%20Logo%20White..png";

const Sidebar = ({ collapsed, onToggle, onMobileClose }) => {
  const { user } = useAuth();
  const userRole = user?.role || "";

  const visibleItems = NAV_ITEMS.filter(item => item.roles.includes(userRole));

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-50 flex flex-col bg-sidebar text-sidebar-foreground transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo Section */}
      <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="Avida" 
              className="h-10 w-auto"
            />
          </div>
        )}
        {collapsed && (
          <img 
            src={LOGO_URL} 
            alt="Avida" 
            className="h-8 w-auto mx-auto"
          />
        )}
        <button
          onClick={onToggle}
          className="hidden lg:flex p-1.5 rounded-md hover:bg-sidebar-hover transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
        <button
          onClick={onMobileClose}
          className="lg:hidden p-1.5 rounded-md hover:bg-sidebar-hover transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-2">
          {visibleItems.map((item) => (
            <li key={item.href}>
              <NavLink
                to={item.href}
                onClick={onMobileClose}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-sidebar-active text-white border-l-2 border-avida-yellow"
                      : "text-slate-300 hover:bg-sidebar-hover hover:text-white"
                  )
                }
              >
                <item.icon className={cn("h-5 w-5 flex-shrink-0", collapsed && "mx-auto")} />
                {!collapsed && <span>{item.title}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User Section */}
      {!collapsed && (
        <div className="border-t border-sidebar-border p-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-avida-blue flex items-center justify-center text-white font-semibold text-sm">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-400 truncate">{user?.role}</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

const Header = ({ onMenuClick, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b border-slate-200 bg-white px-4 lg:px-6">
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 rounded-md hover:bg-slate-100"
      >
        <Menu className="h-5 w-5 text-slate-600" />
      </button>
      
      <div className="flex-1">
        {title && (
          <h1 className="font-heading text-xl font-semibold text-slate-900">{title}</h1>
        )}
      </div>

      <div className="flex items-center gap-3">
        <span className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-600">
          <span className="px-2 py-1 rounded-full bg-avida-blue-50 text-avida-blue-700 text-xs font-medium">
            {user?.role}
          </span>
          <span className="hidden md:inline">{user?.email}</span>
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="text-slate-600 hover:text-slate-900"
        >
          <LogOut className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">Logout</span>
        </Button>
      </div>
    </header>
  );
};

const Layout = ({ children, title }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          onMobileClose={() => {}}
        />
      </div>

      {/* Sidebar - Mobile */}
      <div
        className={cn(
          "lg:hidden",
          mobileMenuOpen ? "block" : "hidden"
        )}
      >
        <Sidebar
          collapsed={false}
          onToggle={() => {}}
          onMobileClose={() => setMobileMenuOpen(false)}
        />
      </div>

      {/* Main Content */}
      <div
        className={cn(
          "min-h-screen transition-all duration-300",
          sidebarCollapsed ? "lg:pl-16" : "lg:pl-64"
        )}
      >
        <Header
          onMenuClick={() => setMobileMenuOpen(true)}
          title={title}
        />
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
